import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  Alert,
  Button,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import "./ViewClaims.css";
import { Loader } from "../../../components/loader/Loader";
import { capitalize } from "../../../utils/utils";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import {getClaims} from "../../../services/claimService";
import Header from "../../../components/dms_common/Header";
import {getDatesFromLocalStorage, updateDatesInLocalStorage} from "../../../components/dms_common/DmsCommonServices";
import { popupType } from "../../../types/types";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { OpenClaimsColumnsMetaData} from "../utils/ViewClaimsUtils";
import {getLastNPathSegments} from "../utils/utils";
import MiniClaimSummary from "../components/MiniClaimSummary";
import MultiCategoryFilter from "../../../components/dms_common/MultiCategoryFilter";
import { getUTCDate } from "../../../services/manageOrderService";
import { isGapMoreThanNDays } from "../../../utils/calenderUtils";
import { setSearchText} from "../../../stateManagement/actions/claims";
import Search from "../../../components/dms_common/Search";
import { getConfigData } from "../../../services/configurationService";
import { logLoadTrace } from "../../../../src/firebase";

type Counts = Record<string, number>;
const ClosedClaims = () => {
  const feature = "Claims";
  const pageID = "viewClaimStatus";
  const {searchText} = useSelector((state: any) => state.claims);
  const dispatch = useDispatch();
  const path = getLastNPathSegments(window.location.href, 2);
  const lastSegment=path[0];
  
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [isDateActionLoading, setIsDateActionLoading] = useState(false);
  const [claimStagesList, setClaimStagesList] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");
  const [selectedClaimTypes, setSelectedClaimTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [claimTypeCounts, setClaimTypeCounts] = useState<Counts>({});
  const [statusCounts, setStatusCounts] = useState<Counts>({});
  const [selectedFilterType, setSelectedFilterType] = useState<string>("type");
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {claimTypes, claimSubTypes, claimTypesObj} = ConfigDictionary;
  const statuses = claimStagesList.filter(stage => stage.id !== "ALL" && stage.id !== "Requested").map(stage => stage.id);
  const claimTypesFilters = Object.values(claimTypesObj)
  .flatMap((type:any) => Object.values(type.subTypes).map((subType:any) => subType.label));

  const calculateClaimCounts = (data: any[]) => {
    const claimTypeCounts: Counts = {};
    const statusCounts: Counts = {};
    
    claimTypesFilters.forEach((type) => (claimTypeCounts[type] = 0));
    statuses.forEach((status) => (statusCounts[status] = 0));
  
    data.forEach((item) => {
      const baseClaimType = item.claimType.split(" - ")[1];
  
      if (claimTypesFilters.includes(baseClaimType)) {
        claimTypeCounts[baseClaimType]++;
      }
  
      const baseStatus = statuses.find(
        (status) => status.toLowerCase() === item.status.toLowerCase()
      );
  
      if (baseStatus) {
        statusCounts[baseStatus]++;
      }
    });
  
    return { claimTypeCounts, statusCounts };
  };
  useEffect(() => {
    const { claimTypeCounts, statusCounts } = calculateClaimCounts(data);
    setClaimTypeCounts(claimTypeCounts);
    setStatusCounts(statusCounts);
  }, [data]);

  const handleClearAll = async () => {
    if (isDateActionLoading) return;
    setIsDateActionLoading(true);
    try {
      const storedData = localStorage.getItem("claims");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData.viewClaimStatus) {
          const { startDate, endDate, ...rest } = parsedData.viewClaimStatus;
          parsedData.viewClaimStatus = rest;
          localStorage.setItem("claims", JSON.stringify(parsedData));
        }
      }
      const currentDate = dayjs();
      const adjustedStartDate = currentDate.startOf("month");
      setStartDate(adjustedStartDate);
      setEndDate(currentDate);
  
      dispatch(setSearchText(""));
      const start = getUTCDate(adjustedStartDate, "start");
      const end = getUTCDate(currentDate, "end");
      await getAllClaims(start, end);
    } finally {
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsDateActionLoading(false);
    }
  };

  useEffect(() => {
    const filteredData = filterData(data, searchText);
    setRows(filteredData);
  }, [selectedClaimTypes, selectedStatuses, searchText, data]);

  const handleSearchValueChange = useCallback((newValue: string) => {
    dispatch(setSearchText(newValue));
  }, [dispatch]);

  const filters = [
    {
      id: 'type',
      label: 'Type',
      options: claimTypesFilters.map(type => ({
        id: type,
        label: type,
        count: claimTypeCounts[type] || 0
      })),
      selectedOptions: selectedClaimTypes,
      setSelectedOptions: setSelectedClaimTypes,
    },
    {
      id: 'status',
      label: 'Status',
      options: statuses.map(status => ({
        id: status,
        label: status,
        count: statusCounts[status] || 0
      })),
      selectedOptions: selectedStatuses,
      setSelectedOptions: setSelectedStatuses,
    }
  ];
  const filterData = (data: any[], searchText: string) => {
    return data.filter((item: any) => {
      const baseClaimType = item.claimType.split(" - ")[1];
      const baseStatus = item.status.toLowerCase();
      const matchesSearch = searchText.length === 0 || 
        Object.values(item).some((value: any) => 
          typeof value === "string" && 
          value.toLowerCase().includes(searchText.toLowerCase())
        );

      const matchesClaimType =
        selectedClaimTypes.length === 0 ||
        selectedClaimTypes.includes(baseClaimType);

      const matchesStatus =
        selectedStatuses.length === 0 ||
        selectedStatuses.map((s) => s.toLowerCase()).includes(baseStatus);

      return matchesSearch && matchesClaimType && matchesStatus;
    });
  };
  const filteredData = useMemo(() => {
    return filterData(data, searchText);
  }, [searchText, data]);
  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);
  const getAllClaims = async (startDate: string, endDate: string) => {
    if(loader) return;
    setLoader(true);
    try {
      const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => capitalize(txt.toLowerCase()));

      const formatClaimSubType = (subType: string) => {
        if (subType === "BANNER") {
          return claimSubTypes[4];
        } else if (subType === "ASSET") {
          return claimSubTypes[5];
        } else if (subType === "ALLEXCLTOV")
        {
          return claimSubTypes[2];
        }
        else if (subType === "TOTALORDERVALUE")
          {
            return claimSubTypes[3];
          }
        else {
          return subType
            .replace(/(RETURN|RETURNS)/gi, " Return")
            .split(" ")
            .map(toTitleCase)
            .join(" ");
        }
      };

      const response = await getClaims(startDate.replace(' ', 'T'), endDate.replace(' ', 'T'), undefined, false, true);

      if (response.length > 0) {
        let responseArray: any[] = [];
        const requestedClaims = response.filter((item: any) => 
          item.status  !== "REQUESTED"
        );
        requestedClaims.forEach((item: any) => {
          const {
            claimId,
            creationTime,
            lastModifiedTime,
            supplier,
            supplierName,
            claimType,
            claimSubType,
            claimTotalValue,
            claimTotalQuantity,
            claimApprovedValue,
            status,
          } = item;
          const claimMonth = dayjs(creationTime).format("MMM-YYYY");
          const claimDate = dayjs(creationTime).format("DD-MM-YYYY");
        
          let formattedClaimType = "";
          if (claimType === claimTypes[2].toUpperCase()) {
            formattedClaimType = `${toTitleCase(claimType)} - ${formatClaimSubType(claimSubType)}`;
          } else if (claimType === claimTypes[0].toUpperCase().replace(" ","")) {
            formattedClaimType = claimTypes[0] + (claimSubType ? ` - ${formatClaimSubType(claimSubType)}` : "");
          } else if(claimType=== claimTypes[1].toUpperCase().replace(" ",""))
          {
            formattedClaimType = claimTypes[1] + (claimSubType ? ` - ${formatClaimSubType(claimSubType)}` : "");
          }
          else {
            formattedClaimType = `${toTitleCase(claimType)} - ${formatClaimSubType(claimSubType)}`;
          }

          let totalCase = 0;
          let totalPcs = 0;
          let totalOthers = 0;

          if (claimType !== claimTypes[2].toUpperCase()) {
            [totalCase, totalPcs, totalOthers] = claimTotalQuantity ? claimTotalQuantity.split(".").map(Number) : [0, 0, 0];
          } else { totalCase = claimTotalQuantity || '-'; }
          
          const formattedStatus = status.includes("_") ? status.split("_").join(" ") : status;
          responseArray.push({
            id: claimId,
            claimDate,
            distributorId: supplier,
            distributorName: supplierName,
            claimType: formattedClaimType,
            claimMonth,
            claimAmount: claimTotalValue,
            claimApprovedValue,
            totalCase,
            totalPcs,
            totalOthers,
            status: formattedStatus,
            lastModifiedTime,
            includeQty: claimType !== claimTypes[2].toUpperCase()
          });
        });

        responseArray.sort((a, b) => {
          const timeA = dayjs(a.lastModifiedTime);
          const timeB = dayjs(b.lastModifiedTime);
          return timeB.isBefore(timeA) ? -1 : 1;
        });

        setRows(responseArray);
        setData(responseArray);
        const {claimTypeCounts, statusCounts} = calculateClaimCounts(responseArray);
        setClaimTypeCounts(claimTypeCounts);
        setStatusCounts(statusCounts);
      } else {
        setRows([]);
        setData([]);
      }
    } catch (error) {
      setRows([]);
      openPopup("Data Is Not Available.", "Error");
    } finally {
      setLoader(false);
    }
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const isStartDateIsGreaterThanEndDate = (
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ) => {
    const diff = endDate.diff(startDate);
    return diff < 0;
  };

  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      openPopup(`You can't select date range more than ${validDateRange} days!`, "Alert");
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      openPopup("To Date should be greater than from date.", "Alert");
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const handleApply = async () => {
    if (isDateActionLoading) return;
    setIsDateActionLoading(true);
    try {
      if (canWeMakeRequest()) {
        const start = getUTCDate(startDate, "start");
        const end = getUTCDate(endDate, "end");
        dispatch(setSearchText(""));
        await getAllClaims(start, end);
      }
    } finally {
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsDateActionLoading(false);
    }
  };

  const getInitalTableData = async () => {
    const { startDate, endDate } = getDatesFromLocalStorage(feature, pageID);
    let adjustedStartDate = startDate;
    if (startDate.isSame(endDate, "day")) {
      adjustedStartDate = dayjs().startOf("month");
    }
    setStartDate(adjustedStartDate);
    setEndDate(endDate || dayjs());
    const start = getUTCDate(adjustedStartDate, "start");
    const end = getUTCDate((endDate || dayjs()), "end");
    const savedState = getSearchFilterStateFromLocalStorage();
    if (savedState) {
      setSelectedClaimTypes(savedState.selectedClaimTypes);
      setSelectedStatuses(savedState.selectedStatuses);
    }
    await getAllClaims(start, end);
  };

  const fetchRequiredConfigs = async () => {
    try {
      const claimStages = await getConfigData(
        "dms_configuration",
        "claimStages"
      );
      setClaimStagesList(claimStages[0]?.configValue ?? []);
    } catch (error) {
      console.error("Error fetching configs:", error);
      setClaimStagesList([]);
    }
  };
  
  const getSearchFilterStateFromLocalStorage = () => {
    const storedData = localStorage.getItem("claims");
    if (!storedData) return null;
    
    try {
      const parsedData = JSON.parse(storedData);
      const pageData = parsedData.viewClaimStatus;
      if (!pageData) return null;
      
      return {
        selectedClaimTypes: pageData.selectedClaimTypes || [],
        selectedStatuses: pageData.selectedStatuses || []
      };
    } catch {
      return null;
    }
  };

  const updateSearchFilterStateInLocalStorage = (state: {
    selectedClaimTypes: string[];
    selectedStatuses: string[];
  }) => {
    const storedData = localStorage.getItem("claims");
    let parsedData = storedData ? JSON.parse(storedData) : {};
    
    if (!parsedData.viewClaimStatus) {
      parsedData.viewClaimStatus = {};
    }
    
    parsedData.viewClaimStatus = {
      ...parsedData.viewClaimStatus,
      selectedClaimTypes: state.selectedClaimTypes,
      selectedStatuses: state.selectedStatuses
    };
    
    localStorage.setItem("claims", JSON.stringify(parsedData));
  };

  const init = async () => {
    setLoader(true);
    await Promise.allSettled([fetchRequiredConfigs(), getInitalTableData()]).finally(
      () => {
        setLoader(false);
      }
    );
  };
  useEffect(() => {
    dispatch(setSearchText(""));
    logLoadTrace(init, "closed_claims_page_load_time");
  }, []);

  useEffect(() => {
    updateSearchFilterStateInLocalStorage({
      selectedClaimTypes,
      selectedStatuses
    });
  }, [selectedClaimTypes, selectedStatuses]);
  
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('claimsStatus/closed')) {
        dispatch(setSearchText(""));
        setSelectedClaimTypes([]);
        setSelectedStatuses([]);
        localStorage.removeItem("claims");
      }
    };
  }, []);

  return (
    <div className="view_claims_container">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
      <header className="top-heading" style={{ marginBottom: "0px"}}>
      <Header title={"Closed Claims"}/>
        <div style={{ width: "280px"}}>
        <Search searchFunction={handleSearchValueChange} value={searchText}/>
      </div>
      </header>

      <div className="view_claims_filter_searchbar">
        <div className="view_claims_support_utility_dates">
          <Stack spacing={2.8} direction={"row"}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                inputFormat="DD-MM-YYYY"
                value={startDate}
                maxDate={dayjs()}
                onChange={(value) => {
                  if (value) {
                    setStartDate(value);
                  }
                }}
                renderInput={(props) => <TextField {...props} disabled size="small" sx={{
                  "& .MuiInputBase-root": { color: "#252525" },
                  "& .MuiInputLabel-root": { color: "#131523" },
                  "& .MuiSvgIcon-root": { color: "#227C9D" },
                }} />}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                inputFormat="DD-MM-YYYY"
                value={endDate}
                maxDate={dayjs()}
                onChange={(value) => {
                  if (value) {
                    setEndDate(value);
                  }
                }}
                renderInput={(props) => <TextField {...props} disabled size="small" sx={{
                  "& .MuiInputBase-root": { color: "#252525" },
                  "& .MuiInputLabel-root": { color: "#131523" },
                  "& .MuiSvgIcon-root": { color: "#227C9D" },
                }} />}
              />
            </LocalizationProvider>
          </Stack>
          <div className="view_claims_reset_and_apply_button">
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#237C9D",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
              onClick={handleApply}
              disabled={isDateActionLoading}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              sx={{
                backgroundColor: "#f0fbff",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
              onClick={handleClearAll}
              disabled={isDateActionLoading}
            >
              Reset
            </Button>
          </div>
        </div>
        <MultiCategoryFilter
          isOpen={filterOpen}
          setIsOpen={setFilterOpen}
          activeFilter={selectedFilterType}
          setActiveFilter={setSelectedFilterType}
          filters={filters}
          label="Type & Status"
          searchValue={filterSearchValue}
          onSearchChange={setFilterSearchValue}
          showSearch={true}
        />
      </div>
      {loader ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) : (
        <>
          <span>
            <MiniClaimSummary rows={rows} />
          </span>
          <CustomDataGrid
            rows={rows}
            columnsMetaData={OpenClaimsColumnsMetaData}
            orderStatusConfig={claimStagesList}
            redirectUrl="/claimsStatus/closed/details?claimId="
            tableWidth={0.75}
          />
        </>
      )}
    </div>
  );
};

export default ClosedClaims;